import { axios, getResponse, isUndNullEmp, settings } from '../../helpers/main';

const objectService = getResponse({type: 0}); //eslint-disable-line

export const SaleService = {
    /**
     * Obtiene los totales de cuentas por pagar por empresa.
     * @param {Object} params
     * @param {string} params.token - Token de autenticación del usuario.
     * @param {string} params.languageId - ID del lenguaje actual.
     * @param {string} [params.subMethod] - Nombre del submétodo para trazabilidad (opcional).
     * @returns {Promise<objectService>} Respuesta con los datos de las empresas y totales.
     */
    getBillToPayAsync: async ({
        token = '',
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'SaleService - getBillToPayAsync';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.sales}/`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    method: settings?.backend?.methods?.sales?.getBillToPay,
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Obtiene los detalles de una empresa específica por su ID.
     * @param {Object} params
     * @param {string} params.token - Token de autenticación del usuario.
     * @param {number} params.id - ID de la empresa.
     * @param {string} params.languageId - ID del lenguaje actual.
     * @param {string} [params.subMethod] - Nombre del submétodo para trazabilidad (opcional).
     * @returns {Promise<objectService>} Respuesta con los detalles de la empresa.
     */
    getCompanyDetailsByIdAsync: async ({
        token = '',
        id = 0,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'SaleService - getCompanyDetailsByIdAsync';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.companies}/`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    id: id,
                    languageId: languageId,
                    method: settings?.backend?.methods?.companies.getById,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Obtiene los clientes y balances de una empresa específica.
     * @param {Object} params
     * @param {string} params.token - Token de autenticación del usuario.
     * @param {number} params.companyId - ID de la empresa.
     * @param {string} params.languageId - ID del lenguaje actual.
     * @param {string} [params.subMethod] - Nombre del submétodo para trazabilidad (opcional).
     * @returns {Promise<objectService>} Respuesta con los clientes y totales.
     */
    getBillToPayCompanyCustomersAsync: async ({
        token = '',
        companyId = 0,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'SaleService - getBillToPayCompanyCustomersAsync';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.sales}/`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    data: { companyId: companyId },
                    method: settings?.backend?.methods?.sales?.getBillToPayCompanyCustomers,
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Obtiene los estados de cuenta de un cliente específico en una empresa.
     * @param {Object} params
     * @param {string} params.token - Token de autenticación del usuario.
     * @param {number} params.companyId - ID de la empresa.
     * @param {number} params.customerId - ID del cliente.
     * @param {string} params.languageId - ID del lenguaje actual.
     * @param {string} [params.subMethod] - Nombre del submétodo para trazabilidad (opcional).
     * @returns {Promise<objectService>} Respuesta con los datos del estado de cuenta.
     */
    getBillToPayCompanyCustomerAsync: async ({
        token = '',
        companyId = 0,
        customerId = 0,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'SaleService - getBillToPayCompanyCustomerAsync';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.sales}/`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    data: { companyId: companyId, customerId: customerId },
                    method: settings?.backend?.methods?.sales?.getBillToPayCompanyCustomer,
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Obtiene el detalle de un documento específico (Cabecera, Líneas y Digitalizados).
     * @param {Object} params
     * @param {string} params.token - Token de autenticación.
     * @param {number} params.companyId - ID de la empresa.
     * @param {number} params.customerId - ID del cliente.
     * @param {string} params.docType - Tipo de documento.
     * @param {number} params.docNumber - Número del documento.
     * @param {string} params.languageId - ID del lenguaje.
     * @param {string} [params.subMethod] - Submétodo para trazabilidad.
     * @returns {Promise<objectService>} Respuesta con los datos del documento.
     */
    getDetailDocumentsAsync: async ({
        token = '',
        companyId = 0,
        customerId = 0,
        docType = '',
        docNumber = 0,
        languageId = 'en',
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'SaleService - getDetailDocumentsAsync';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.sales}/`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    data: {
                        companyId: companyId,
                        customerId: customerId,
                        docType: docType,
                        docNumber: docNumber,
                    },
                    method: settings?.backend?.methods?.sales?.getDetailDocuments,
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },
};
